/**
 * Reads a dot-path (e.g. "officers.items" or "officers.items.2") out of a plain object.
 * Returns undefined if any segment along the way is missing. Callers that know the shape at
 * the target path can supply T to avoid re-casting at the call site.
 */
export function getNestedValue<T = unknown>(obj: unknown, path: string): T | undefined {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc === null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj) as T | undefined;
}

/**
 * Builds the FormActionState.formData patch needed to replace the value at a dot-path
 * (e.g. "officers.items") with newValue, preserving every sibling field along the path.
 * SetFormActionWithId's reducer only shallow-merges at formData's top level, so the
 * returned object is keyed by just the path's first segment, fully reconstructed -
 * dispatch it as-is (`{ formData: buildNestedPatch(...) }`) rather than deep-merging further.
 */
export function buildNestedPatch(
  currentFormData: Record<string, unknown> | undefined,
  path: string,
  newValue: unknown,
): Record<string, unknown> {
  const segments = path.split('.');
  const topKey = segments[0];

  if (segments.length === 1) {
    return { ...currentFormData, [topKey]: newValue };
  }

  const topValue: Record<string, unknown> = {
    ...((currentFormData?.[topKey] as Record<string, unknown>) ?? {}),
  };
  let cursor: Record<string, unknown> = topValue;
  for (let i = 1; i < segments.length - 1; i++) {
    const segment = segments[i];
    cursor[segment] = { ...((cursor[segment] as Record<string, unknown>) ?? {}) };
    cursor = cursor[segment] as Record<string, unknown>;
  }
  cursor[segments[segments.length - 1]] = newValue;

  return { ...currentFormData, [topKey]: topValue };
}

function isPlainRecursableObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);
}

/**
 * True for undefined/null/''/a NaN Date/a plain object with no own keys ({} - the shape an
 * unfilled composite form field, e.g. a Date-of-birth group, is left at) - anything that isn't
 * a real, usable value. Arrays and Date instances with a real value are never blank.
 */
export function isBlankValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (value instanceof Date) return Number.isNaN(value.getTime());
  if (isPlainRecursableObject(value)) return Object.keys(value).length === 0;
  return false;
}

/**
 * Deep-merges incoming values into existing ones, but only where existing is missing/blank -
 * never overwrites a field the user has already filled in. Used when re-seeding a record from an
 * external source (e.g. re-selecting the same Companies House officer) so it doesn't clobber
 * edits made since the last time it was seeded.
 */
export function fillBlanks(
  existing: Record<string, unknown> | undefined,
  incoming: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...(existing ?? {}) };

  for (const key of Object.keys(incoming)) {
    const incomingValue = incoming[key];
    const existingValue = existing?.[key];

    if (isPlainRecursableObject(incomingValue) && (existingValue === undefined || isPlainRecursableObject(existingValue))) {
      merged[key] = fillBlanks(existingValue as Record<string, unknown> | undefined, incomingValue);
    } else if (isBlankValue(existingValue)) {
      merged[key] = incomingValue;
    }
  }

  return merged;
}
